import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Card, CardContent, Divider, Button,
  IconButton, LinearProgress, Alert, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  ContactPage as ContactPageIcon,
  ContactPhone as ContactPhoneIcon,
  Work as WorkIcon,
  Home as HomeIcon,
  School as SchoolIcon,
  Cake as CakeIcon,
  Public as PublicIcon,
  FamilyRestroom as FamilyIcon,
  AttachMoney as AttachMoneyIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { usePermissions } from '../../../utils/permissions';

// Isolated input component that doesn't trigger parent re-renders
const IsolatedInput = React.memo(({ field, value, onChange, placeholder, multiline = false, rows = 1, type = 'text', min, style, disabled = false }) => {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    if (disabled) return;
    const newValue = e.target.value;
    setLocalValue(newValue);
  };

  const handleBlur = () => {
    if (!disabled) {
      onChange(field, localValue);
    }
  };

  if (multiline) {
    return (
      <textarea
        ref={inputRef}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
              style={{
        width: '100%',
        border: 'none',
        outline: 'none',
        background: '#f8f9fa',
        padding: '8px 12px',
        fontSize: '0.875rem',
        fontFamily: 'inherit',
        color: 'inherit',
        resize: 'none',
        borderRadius: '4px',
        marginLeft: '8px',
        ...style
      }}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      min={min}
      disabled={disabled}
      style={{
        width: '100%',
        border: 'none',
        outline: 'none',
        background: '#f8f9fa',
        padding: '8px 12px',
        fontSize: '0.875rem',
        fontFamily: 'inherit',
        color: 'inherit',
        borderRadius: '4px',
        marginLeft: '8px',
        ...style
      }}
    />
  );
});

const GENDERS = [
  'Male',
  'Female', 
  'Non-binary',
  'Prefer not to say',
  'Other'
];

const MARITAL_STATUS_OPTIONS = [
  'Single',
  'Married',
  'Divorced',
  'Widowed',
  'Separated',
  'Domestic Partnership'
];

const CERTIFICATE_LEVELS = [
  'High School Diploma',
  'Associate Degree',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'Doctoral Degree',
  'Professional Certificate',
  'Trade Certification',
  'Other'
];

export default function PersonalTab({ formData, onInputChange, companyId }) {
  const { canEditPersonalDetails, canViewCitizenshipIdentity, canEditCitizenshipIdentity, canDeletePersonalDetails } = usePermissions();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showSSN, setShowSSN] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Generate unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${companyId}/work-permits/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('employee-documents')
        .upload(filePath, file, {
          onUploadProgress: (progress) => {
            setUploadProgress((progress.loaded / progress.total) * 100);
          }
        });

      if (uploadError) throw uploadError;

      // Update form data
      onInputChange('work_permit_document_path', filePath);
      onInputChange('work_permit_document_name', file.name);

    } catch (error) {
      console.error('Error uploading work permit:', error);
      setError('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async () => {
    if (!formData.work_permit_document_path || !canDeletePersonalDetails()) return;
    
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!canDeletePersonalDetails()) return;
    
    try {
      // Delete from storage
      const { error } = await supabase.storage
        .from('employee-documents')
        .remove([formData.work_permit_document_path]);

      if (error) throw error;

      // Clear form data
      onInputChange('work_permit_document_path', '');
      onInputChange('work_permit_document_name', '');

    } catch (error) {
      console.error('Error deleting work permit:', error);
      setError('Error deleting file: ' + error.message);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleViewDocument = async () => {
    if (!formData.work_permit_document_path) return;

    try {
      const { data, error } = await supabase.storage
        .from('employee-documents')
        .createSignedUrl(formData.work_permit_document_path, 3600);

      if (error) throw error;

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error viewing work permit:', error);
      setError('Error viewing file: ' + error.message);
    }
  };

  const handleDownloadDocument = async () => {
    if (!formData.work_permit_document_path) return;

    try {
      const { data, error } = await supabase.storage
        .from('employee-documents')
        .download(formData.work_permit_document_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = formData.work_permit_document_name || 'work-permit.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading work permit:', error);
      setError('Error downloading file: ' + error.message);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Private Contact Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <ContactPhoneIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Private Contact
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Personal Email
              </Typography>
              <IsolatedInput 
                field="personal_email"
                value={formData.personal_email || ''}
                onChange={onInputChange}
                placeholder="john.doe@gmail.com"
                type="email"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Personal Phone
              </Typography>
              <IsolatedInput 
                field="personal_phone"
                value={formData.personal_phone || ''}
                onChange={onInputChange}
                placeholder="+1 (555) 123-4567"
                type="tel"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Emergency Contact Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <ContactPageIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Emergency Contact
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Contact Name
              </Typography>
              <IsolatedInput 
                field="emergency_contact_name"
                value={formData.emergency_contact_name || ''}
                onChange={onInputChange}
                placeholder="Jane Doe"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Contact Phone
              </Typography>
              <IsolatedInput 
                field="emergency_contact_phone"
                value={formData.emergency_contact_phone || ''}
                onChange={onInputChange}
                placeholder="+1 (555) 987-6543"
                type="tel"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Work Permit Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <WorkIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Work Permit
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Permit Number
              </Typography>
              <IsolatedInput 
                field="work_permit_no"
                value={formData.work_permit_no || ''}
                onChange={onInputChange}
                placeholder="WP123456789"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Expiration Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.work_permit_expiration ? dayjs(formData.work_permit_expiration) : null}
                  onChange={(newValue) => {
                    onInputChange('work_permit_expiration', newValue ? newValue.format('YYYY-MM-DD') : '');
                  }}
                  disabled={!canEditPersonalDetails()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                Work Permit Document
              </Typography>
              
              {!formData.work_permit_document_path ? (
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleFileSelect}
                    disabled={uploading || !canEditPersonalDetails()}
                    size="small"
                  >
                    {uploading ? 'Uploading...' : 'Upload Work Permit'}
                  </Button>
                  
                  {uploading && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(uploadProgress)}% uploaded
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formData.work_permit_document_name}
                    </Typography>
                    <Chip
                      label={formData.work_permit_document_name?.split('.').pop()?.toUpperCase() || 'FILE'}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Box>
                    <IconButton
                      onClick={handleViewDocument}
                      title="View"
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleDownloadDocument}
                      title="Download"
                      size="small"
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleDeleteDocument}
                      title="Delete"
                      size="small"
                      color="error"
                      disabled={!canDeletePersonalDetails()}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Address Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <HomeIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Home Address
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Street Address
              </Typography>
              <IsolatedInput 
                field="street_address"
                value={formData.street_address || ''}
                onChange={onInputChange}
                placeholder="123 Main Street"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                City
              </Typography>
              <IsolatedInput 
                field="city"
                value={formData.city || ''}
                onChange={onInputChange}
                placeholder="New York"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                State/Province
              </Typography>
              <IsolatedInput 
                field="state"
                value={formData.state || ''}
                onChange={onInputChange}
                placeholder="NY"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                ZIP/Postal Code
              </Typography>
              <IsolatedInput 
                field="zip_code"
                value={formData.zip_code || ''}
                onChange={onInputChange}
                placeholder="10001"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Country
              </Typography>
              <IsolatedInput 
                field="country"
                value={formData.country || ''}
                onChange={onInputChange}
                placeholder="United States"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Education Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <SchoolIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Education
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Certificate Level
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={formData.certificate_level || ''}
                  onChange={(e) => {
                    onInputChange('certificate_level', e.target.value);
                  }}
                  disabled={!canEditPersonalDetails()}
                  displayEmpty
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-select': { py: 0.5 }
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: '#999' }}>Select Level</em>
                  </MenuItem>
                  {CERTIFICATE_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Field of Study
              </Typography>
              <IsolatedInput 
                field="field_of_study"
                value={formData.field_of_study || ''}
                onChange={onInputChange}
                placeholder="Computer Science"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Birth Information Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <CakeIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Birth Information
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Legal Name
              </Typography>
              <IsolatedInput 
                field="legal_name"
                value={formData.legal_name || ''}
                onChange={onInputChange}
                placeholder="Full legal name"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Date of Birth
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.date_of_birth ? dayjs(formData.date_of_birth) : null}
                  onChange={(newValue) => {
                    onInputChange('date_of_birth', newValue ? newValue.format('YYYY-MM-DD') : '');
                  }}
                  disabled={!canEditPersonalDetails()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Gender
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={formData.gender || ''}
                  onChange={(e) => {
                    onInputChange('gender', e.target.value);
                  }}
                  disabled={!canEditPersonalDetails()}
                  displayEmpty
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-select': { py: 0.5 }
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: '#999' }}>Select Gender</em>
                  </MenuItem>
                  {GENDERS.map((gender) => (
                    <MenuItem key={gender} value={gender}>
                      {gender}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Visa Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <PublicIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Visa Information
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Visa Number
              </Typography>
              <IsolatedInput 
                field="visa_no"
                value={formData.visa_no || ''}
                onChange={onInputChange}
                placeholder="V123456789"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Expiration Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.visa_expiration_date ? dayjs(formData.visa_expiration_date) : null}
                  onChange={(newValue) => {
                    onInputChange('visa_expiration_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                  }}
                  disabled={!canEditPersonalDetails()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Citizenship Section */}
      {canViewCitizenshipIdentity() && (
        <Card sx={{
          mb: 4,
          borderRadius: 3,
          border: `1px solid rgba(0, 0, 0, 0.08)`,
          background: 'white',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
                border: '1px solid rgba(25, 118, 210, 0.2)'
              }}>
                <PublicIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                Citizenship & Identity
              </Typography>
            </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                  Nationality
                </Typography>
                <IsolatedInput 
                  field="nationality"
                  value={formData.nationality || ''}
                  onChange={onInputChange}
                  placeholder="American"
                  disabled={!canEditCitizenshipIdentity()}
                  style={{ opacity: canEditCitizenshipIdentity() ? 1 : 0.6, cursor: canEditCitizenshipIdentity() ? 'text' : 'not-allowed' }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                  ID Number
                </Typography>
                <IsolatedInput 
                  field="identification_no"
                  value={formData.identification_no || ''}
                  onChange={onInputChange}
                  placeholder="ID123456789"
                  disabled={!canEditCitizenshipIdentity()}
                  style={{ opacity: canEditCitizenshipIdentity() ? 1 : 0.6, cursor: canEditCitizenshipIdentity() ? 'text' : 'not-allowed' }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                  SSN Number
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                  <IsolatedInput 
                    field="ssn_no"
                    value={formData.ssn_no || ''}
                    onChange={onInputChange}
                    type={showSSN ? "text" : "password"}
                    placeholder="***-**-****"
                    disabled={!canEditCitizenshipIdentity()}
                    style={{ opacity: canEditCitizenshipIdentity() ? 1 : 0.6, cursor: canEditCitizenshipIdentity() ? 'text' : 'not-allowed' }}
                  />
                  <IconButton
                    onClick={() => setShowSSN(!showSSN)}
                    title={showSSN ? "Hide SSN" : "Show SSN"}
                    size="small"
                    disabled={!canEditCitizenshipIdentity()}
                    sx={{ opacity: canEditCitizenshipIdentity() ? 1 : 0.6 }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                  Passport Number
                </Typography>
                <IsolatedInput 
                  field="passport_no"
                  value={formData.passport_no || ''}
                  onChange={onInputChange}
                  placeholder="P123456789"
                  disabled={!canEditCitizenshipIdentity()}
                  style={{ opacity: canEditCitizenshipIdentity() ? 1 : 0.6, cursor: canEditCitizenshipIdentity() ? 'text' : 'not-allowed' }}
                />
              </Box>
            </Grid>
          </Grid>
          </CardContent>
        </Card>
      )}

      {/* Family Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <FamilyIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Family Information
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Marital Status
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={formData.marital_status || ''}
                  onChange={(e) => {
                    onInputChange('marital_status', e.target.value);
                  }}
                  disabled={!canEditPersonalDetails()}
                  displayEmpty
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-select': { py: 0.5 }
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: '#999' }}>Select Status</em>
                  </MenuItem>
                  {MARITAL_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Dependent Children
              </Typography>
              <IsolatedInput 
                field="dependent_children"
                value={formData.dependent_children || 0}
                onChange={onInputChange}
                type="number"
                placeholder="0"
                min="0"
                disabled={!canEditPersonalDetails()}
                style={{ opacity: canEditPersonalDetails() ? 1 : 0.6, cursor: canEditPersonalDetails() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        PaperProps={{
          sx: {
            position: 'fixed !important',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete Work Permit
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{formData.work_permit_document_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteDocument}
            color="error"
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}